import { createContext, useCallback, useContext, useMemo, type ReactNode } from "react";
import { usePlatformData } from "@/store/platform/PlatformDataProvider";
import type { PlatformLocataire } from "@/store/platform/types";
import type { Locataire, LocataireInput, TypePieceIdentite } from "./types";

interface LocatairesContextValue {
  locataires: Locataire[];
  addLocataire: (input: LocataireInput) => Locataire;
  getLocataire: (id: string) => Locataire | undefined;
  removeLocataire: (id: string) => void;
  updateLocataire: (id: string, input: LocataireInput) => void;
}

const LocatairesContext = createContext<LocatairesContextValue | null>(null);

function toOwnerLocataire(source: PlatformLocataire): Locataire {
  return {
    id: source.id,
    prenom: source.prenom,
    nom: source.nom,
    telephone: source.telephone,
    whatsapp: source.whatsapp,
    email: source.email,
    adresse: source.adresse,
    profession: source.profession,
    typePiece: source.typePiece as TypePieceIdentite | "",
    numeroPiece: source.numeroPiece,
    contactUrgence: source.contactUrgence,
    notes: source.notes,
    proprietaireId: source.proprietaireId,
    agenceId: source.agenceId,
    createdAt: source.createdAt,
  };
}

/**
 * Vue « Propriétaire » du store unifié de la plateforme (scope « proprietaire »).
 */
export function LocatairesProvider({ children }: { children: ReactNode }) {
  const platform = usePlatformData();

  const locataires = useMemo(
    () => platform.locataires.filter((item) => item.scope === "proprietaire").map(toOwnerLocataire),
    [platform.locataires]
  );

  const addLocataire = useCallback(
    (input: LocataireInput) => {
      const created = platform.addLocataire({ ...input, scope: "proprietaire" });
      return toOwnerLocataire(created);
    },
    [platform.addLocataire]
  );

  const updateLocataire = useCallback(
    (id: string, input: LocataireInput) => {
      platform.updateLocataire(id, { ...input, scope: "proprietaire" });
    },
    [platform.updateLocataire]
  );

  const removeLocataire = useCallback(
    (id: string) => {
      platform.removeLocataire(id);
    },
    [platform.removeLocataire]
  );

  const getLocataire = useCallback((id: string) => locataires.find((item) => item.id === id), [locataires]);

  const value = useMemo(
    () => ({ locataires, addLocataire, getLocataire, removeLocataire, updateLocataire }),
    [locataires, addLocataire, getLocataire, removeLocataire, updateLocataire]
  );

  return <LocatairesContext.Provider value={value}>{children}</LocatairesContext.Provider>;
}

export function useLocataires(): LocatairesContextValue {
  const context = useContext(LocatairesContext);
  if (!context) {
    throw new Error("useLocataires doit être utilisé à l'intérieur de LocatairesProvider");
  }
  return context;
}